import { reportError } from './report';
import { peekAll } from '../offline/db';

/**
 * Measurements, not failures — added 3 Oct.
 *
 * Catherine Cruz's deliveries sat on her iPhone for minutes, once for twenty
 * hours, while the phone said it was online and her location kept arriving.
 * Her phone also failed eleven times to store a photo; the rest of the fleet,
 * once. Nothing we recorded could say WHY: whether the phone was full, which
 * step of the send was stuck, how much work was waiting on the phone, or how
 * long the app spent in the background. Each of those is a guess without this.
 *
 * Everything here goes through the same reporter as real errors (held on the
 * phone with no signal, never blocking, never throwing), under 'diag_*' kinds
 * that every "app problems" count leaves out. The server gives these their own
 * hourly allowance (migration 082) so they can never crowd out a real error.
 *
 * Nothing here changes what the driver sees or does.
 */

const KEY = 'jw.diag.last';

/** At most once per `everyMs` for `name`, remembered across app restarts. */
function due(name: string, everyMs: number): boolean {
  try {
    const seen = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, number>;
    const now = Date.now();
    if (seen[name] && now - seen[name] < everyMs) return false;
    seen[name] = now;
    localStorage.setItem(KEY, JSON.stringify(seen));
    return true;
  } catch {
    return false;   // storage unusable: say nothing rather than say it every time
  }
}

const mb = (n: number | undefined) => (n === undefined ? null : Math.round(n / 1048576));

/**
 * How much room the app has on this phone. On iPhone the allowance shrinks as
 * the phone fills up, so a small quota with high usage is a full phone. Every
 * two hours at most.
 */
export async function reportStorage(): Promise<void> {
  try {
    if (!navigator.storage?.estimate || !due('storage', 2 * 3600_000)) return;
    const est = await navigator.storage.estimate();
    const persisted = navigator.storage.persisted
      ? await navigator.storage.persisted().catch(() => null) : null;
    reportError('diag_storage', 'app', {
      usedMB: mb(est.usage),
      quotaMB: mb(est.quota),
      freePct: est.quota ? Math.round(100 * (1 - (est.usage ?? 0) / est.quota)) : null,
      persisted,
    });
  } catch { /* diagnostics only */ }
}

/**
 * What is still waiting on the phone to be sent. Only said when something is
 * waiting, and at most every ten minutes.
 */
export async function reportQueue(where: string): Promise<void> {
  try {
    const items = await peekAll();
    if (items.length === 0 || !due('queue', 10 * 60_000)) return;
    const oldest = Math.min(...items.map((i) => i.queuedAt));
    reportError('diag_queue', where, {
      waiting: items.length,
      oldestMin: Math.round((Date.now() - oldest) / 60_000),
      photosHeld: items.filter((i) => i.photoBlob || i.photoBytes).length,
      maxAttempts: Math.max(...items.map((i) => i.attempts ?? 0)),
    });
  } catch { /* diagnostics only */ }
}

/**
 * Time away from the app: screen off, another app in front. Reported on the
 * way back, only for two minutes or more.
 */
function installBackgroundTiming(): void {
  let hiddenAt: number | null = null;
  document.addEventListener('visibilitychange', () => {
    try {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (hiddenAt !== null) {
        const ms = Date.now() - hiddenAt;
        hiddenAt = null;
        if (ms >= 120_000) reportError('diag_background', 'app', { awayMin: Math.round(ms / 60_000) });
        void reportQueue('resume');
        void reportStorage();
      }
    } catch { /* diagnostics only */ }
  });
}

/**
 * Switched off AFTER the check (owner, 4 Oct): "we don't want drivers to pass
 * the test and then turn whatever off." While the app is running this notices,
 * with the time:
 *   - location or camera permission changing (e.g. granted -> denied)
 *   - the phone going offline (airplane mode or no signal) and for how long
 * Permission state is read when it changes (where the phone supports that),
 * every 5 minutes, and whenever the app comes back to the screen.
 */
function installChangeWatch(): void {
  const last: Record<string, string> = {};
  const read = async (name: string) => {
    try {
      const st = await navigator.permissions?.query({ name: name as PermissionName });
      if (!st) return;
      const note = (state: string) => {
        if (last[name] !== undefined && last[name] !== state) {
          reportError('diag_permission', 'app', { name, from: last[name], to: state, visible: document.visibilityState });
        }
        last[name] = state;
      };
      note(st.state);
      st.onchange = () => note(st.state);
    } catch { /* this phone cannot say */ }
  };
  const all = () => { void read('geolocation'); void read('camera'); };
  all();
  setInterval(all, 5 * 60_000);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') all(); });

  let offAt: number | null = null;
  window.addEventListener('offline', () => { offAt = Date.now(); });
  window.addEventListener('online', () => {
    if (offAt === null) return;
    const ms = Date.now() - offAt;
    offAt = null;
    if (ms >= 60_000) {
      reportError('diag_offline', 'app', {
        offMin: Math.round(ms / 60_000),
        from: new Date(Date.now() - ms).toISOString(),
      });
    }
  });
}

/** Called once at start-up. Must never throw. */
export function installDiagnostics(): void {
  try {
    installBackgroundTiming();
    installChangeWatch();
    void reportStorage();
    void reportQueue('start');
  } catch { /* diagnostics only */ }
}
